import type { ElementType, FC, ReactNode } from 'react'

import { Fragment } from 'react'
import { motion } from 'motion/react'
import { cn } from '@/lib/utils'

import { EASE_EXPO } from '@/components/motion/reveal'

interface SplitTextProps {
    text: string
    as?: ElementType
    className?: string
    wordClassName?: string
    emphasisClassName?: string
    delay?: number
    stagger?: number
    duration?: number
    trigger?: 'view' | 'mount'
    id?: string
}

interface Word {
    text: string
    emphasis: boolean
}

// "*word*" marks emphasis, "\n" forces a line break
const parseLines = (text: string): Word[][] => {
    return text.split('\n').map(line => {
        const words: Word[] = []

        line.split('*').forEach((segment, index) => {
            segment.split(' ').filter(Boolean).forEach(word => {
                words.push({ text: word, emphasis: index % 2 === 1 })
            })
        })

        return words
    })
}

const toPlainText = (text: string): string => text.replace(/\*/g, '').replace(/\n/g, ' ').replace(/­/g, '')

export const SplitText: FC<SplitTextProps> = ({
    text,
    as: Tag = 'h2',
    className,
    wordClassName,
    emphasisClassName = 'italic',
    delay = 0,
    stagger = 0.06,
    duration = 1.15,
    trigger = 'view',
    id
}): ReactNode => {
    const lines = parseLines(text)
    let wordIndex = 0

    const variants = {
        hidden: { y: '115%' },
        visible: (index: number) => ({
            y: '0%',
            transition: { duration, delay: delay + index * stagger, ease: EASE_EXPO }
        })
    }

    const triggerProps = trigger === 'mount'
        ? { animate: 'visible' }
        : { whileInView: 'visible', viewport: { once: true, amount: 0.3 } }

    return (
        <Tag id={id} className={className}>
            <span className='sr-only'>
                {toPlainText(text)}
            </span>

            <motion.span aria-hidden='true' className='block' initial='hidden' {...triggerProps}>
                {lines.map((line, lineIndex) => (
                    <Fragment key={lineIndex}>
                        {lineIndex > 0 && <br />}

                        {line.map((word, index) => {
                            const current = wordIndex++

                            return (
                                <Fragment key={`${lineIndex}-${index}`}>
                                    <span className='-my-[0.14em] inline-block overflow-hidden py-[0.14em] align-top'>
                                        <motion.span
                                            custom={current}
                                            variants={variants}
                                            className={cn('inline-block', wordClassName, word.emphasis && emphasisClassName)}
                                        >
                                            {word.text}
                                        </motion.span>
                                    </span>
                                    {index < line.length - 1 && ' '}
                                </Fragment>
                            )
                        })}
                    </Fragment>
                ))}
            </motion.span>
        </Tag>
    )
}
